<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  confirmButtonClass: {
    type: String,
    default: 'bg-red-600 hover:bg-red-700'
  },
  requiredId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['confirm', 'cancel', 'close'])

const isVisible = ref(props.show)
const inputId = ref('')
const errorMessage = ref('')

watch(() => props.show, (newValue) => {
  isVisible.value = newValue
  if (!newValue) {
    inputId.value = ''
    errorMessage.value = ''
  }
})

const isIdValid = computed(() => {
  return inputId.value === props.requiredId.toString()
})

const handleConfirm = () => {
  if (!isIdValid.value) {
    errorMessage.value = 'Please enter the correct ID to confirm'
    return
  }
  emit('confirm')
  emit('close')
}

const handleCancel = () => {
  emit('cancel')
  emit('close')
}

const handleInput = () => {
  errorMessage.value = ''
}
</script>

<template>
  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="isVisible" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50"></div>
  </Transition>

  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
  >
    <div v-if="isVisible" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <i class="fas fa-exclamation-triangle text-red-600"></i>
            </div>
            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
              <h3 class="text-base font-semibold leading-6 text-gray-900">
                {{ title }}
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  {{ message }}
                </p>
              </div>
              
              <!-- ID Input Section -->
              <div class="mt-4">
                <label for="confirm-id" class="block text-sm font-medium text-gray-700 mb-1.5">
                  Enter fixture ID to confirm
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fas fa-hashtag text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    id="confirm-id"
                    v-model="inputId"
                    @input="handleInput"
                    :class="[
                      'block w-full pl-10 pr-4 py-2.5 rounded-lg shadow-sm transition-all duration-200 ease-in-out',
                      'border-2 focus:ring-2 focus:ring-offset-2',
                      errorMessage 
                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500/20' 
                        : 'border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20',
                      'hover:border-gray-300',
                      'focus:outline-none',
                      'text-base'
                    ]"
                    placeholder="Enter fixture ID"
                  />
                  <div 
                    v-if="isIdValid"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                  >
                    <i class="fas fa-check-circle text-green-500"></i>
                  </div>
                </div>
                <p 
                  v-if="errorMessage" 
                  class="mt-2 text-sm text-red-600 flex items-center gap-1.5 animate-fade-in"
                >
                  <i class="fas fa-exclamation-circle"></i>
                  {{ errorMessage }}
                </p>
                <p 
                  v-else-if="inputId && !isIdValid" 
                  class="mt-2 text-sm text-gray-500 flex items-center gap-1.5"
                >
                  <i class="fas fa-info-circle"></i>
                  Enter the fixture ID to confirm deletion
                </p>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              :class="[
                confirmButtonClass,
                'inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto',
                !isIdValid && 'opacity-50 cursor-not-allowed'
              ]"
              @click="handleConfirm"
              :disabled="!isIdValid"
            >
              {{ confirmText }}
            </button>
            <button
              type="button"
              class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Add smooth transition for input focus */
input:focus {
  transform: translateY(-1px);
}

/* Add subtle hover effect */
input:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Add transition for all interactive states */
input {
  transition: all 0.2s ease-in-out;
}
</style> 